import { useCallback, useMemo, useContext, useState } from "react";
import { SnowplowTracker } from "~/utils/snowplow/snowplow_tracker.client";
import { useUrlToken } from "~/hooks/use_url_token";
import { TrackingContext } from "~/contexts/tracking_context";

export enum IdentityType {
  ProfileId = "PROFILE_ID",
  Email = "APPLICANT_EMAIL",
}

export enum SnowplowEvent {
  QuickApplyAutofillSuccess = "quick_apply_autofill_success",
  MyGreenhouseAdShown = "mygreenhouse.ad_shown",
  MyGreenhouseAdClicked = "mygreenhouse.ad_clicked",
}

const THROTTLE_DURATION = 100; // ms, dedupe events, esp. ad_shown mounting re-renders

export type TrackEventType = (
  eventName: SnowplowEvent,
  data: Record<PropertyKey, string | number | undefined>
) => undefined;

export type UpdateTrackingIdentityType = (
  identity: IdentityType,
  data: string | number
) => undefined;

export const useSnowplowTracker = () => {
  const context = useContext(TrackingContext);
  const tracker = useMemo(() => SnowplowTracker && new SnowplowTracker(), []);
  const urlToken = useUrlToken();
  const [_eventHistory, setEventHistory] = useState(
    (Object.keys(SnowplowEvent) as Array<keyof typeof SnowplowEvent>).reduce((acc, key) => {
      return { ...acc, [key]: 0 };
    }, {} as Record<SnowplowEvent, number>)
  );

  const userInfo = useMemo(() => {
    let user = {};
    if (context.jobSeekerProfileId) {
      user = { ...user, user_id: context.jobSeekerProfileId };
    }
    if (context.applicantEmail) {
      user = { ...user, email: context.applicantEmail };
    }
    return user;
  }, [context]);

  const updateTrackingIdentity = useCallback(
    (identity, value) => {
      switch (identity) {
        case IdentityType.ProfileId:
          context.setJobSeekerProfileId && context.setJobSeekerProfileId(value.toString());
          break;
        case IdentityType.Email:
          context.setApplicantEmail && context.setApplicantEmail(value.toString());
          break;
        default:
          return;
      }
    },
    [context]
  ) as UpdateTrackingIdentityType;

  const trackEventThrottledByEventType = useCallback(
    (eventName, data) => {
      const now = Date.now();
      setEventHistory((history) => {
        if (now - history[eventName] < THROTTLE_DURATION) {
          return history;
        } else {
          tracker &&
            tracker.track({
              name: eventName,
              data: {
                ...userInfo,
                ...data,
                job_board_id: urlToken,
                timestamp: now,
              },
            });
          return Object.assign(history, { [eventName]: now });
        }
      });
    },
    [tracker, userInfo, setEventHistory, urlToken]
  ) as TrackEventType;

  return { updateTrackingIdentity, trackEvent: trackEventThrottledByEventType };
};
